// The World clock tile's logic, free of React so it is unit-testable: IANA time
// zones (the browser's own list, Intl.supportedValuesOf), a city name for each,
// the search that adds one, the saved city list, and each city's time, day and
// offset from the user's own zone.

export type ClockCity = { zone: string; label: string };

// Until the user edits the list: their own zone, then these.
const DEFAULT_ZONES = ['America/New_York', 'Europe/London'];

// Without Intl.supportedValuesOf (older engines) the search offers these.
const FALLBACK_ZONES = [
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'America/Toronto',
  'America/Mexico_City', 'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin',
  'Asia/Jerusalem', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tokyo',
  'Australia/Sydney', 'Pacific/Auckland',
];

// Zones the browser lists under an older spelling (CLDR keeps Asia/Calcutta for
// Asia/Kolkata): the city as it is called now.
const RENAMED: Record<string, string> = {
  Asmera: 'Asmara', Calcutta: 'Kolkata', Coral_Harbour: 'Atikokan', Enderbury: 'Kanton', Faeroe: 'Faroe',
  Godthab: 'Nuuk', Ho_Chi_Minh: 'Ho Chi Minh City', Katmandu: 'Kathmandu', Kiev: 'Kyiv', Ponape: 'Pohnpei',
  Rangoon: 'Yangon', Saigon: 'Ho Chi Minh City', Truk: 'Chuuk',
};

// Big cities without a zone of their own (each keeps another city's clock), so
// typing one finds its zone: "Tel Aviv" is Asia/Jerusalem. Offered only where
// the browser knows the zone.
const ALIASES: Array<[string, string]> = [
  ['Atlanta', 'America/New_York'], ['Boston', 'America/New_York'], ['Miami', 'America/New_York'],
  ['Philadelphia', 'America/New_York'], ['Washington, D.C.', 'America/New_York'],
  ['Montreal', 'America/Toronto'], ['Ottawa', 'America/Toronto'],
  ['Dallas', 'America/Chicago'], ['Houston', 'America/Chicago'], ['Minneapolis', 'America/Chicago'],
  ['Calgary', 'America/Edmonton'], ['Salt Lake City', 'America/Denver'],
  ['San Francisco', 'America/Los_Angeles'], ['San Diego', 'America/Los_Angeles'], ['Seattle', 'America/Los_Angeles'],
  ['Rio de Janeiro', 'America/Sao_Paulo'],
  ['Edinburgh', 'Europe/London'], ['Manchester', 'Europe/London'], ['Barcelona', 'Europe/Madrid'],
  ['Frankfurt', 'Europe/Berlin'], ['Hamburg', 'Europe/Berlin'], ['Munich', 'Europe/Berlin'],
  ['Milan', 'Europe/Rome'], ['Geneva', 'Europe/Zurich'],
  ['Tel Aviv', 'Asia/Jerusalem'], ['Haifa', 'Asia/Jerusalem'], ['Abu Dhabi', 'Asia/Dubai'],
  ['Mumbai', 'Asia/Kolkata'], ['New Delhi', 'Asia/Kolkata'], ['Bengaluru', 'Asia/Kolkata'],
  ['Beijing', 'Asia/Shanghai'], ['Shenzhen', 'Asia/Shanghai'], ['Osaka', 'Asia/Tokyo'],
  ['Canberra', 'Australia/Sydney'], ['Wellington', 'Pacific/Auckland'],
];

const fmtCache = new Map<string, Intl.DateTimeFormat>();
// One formatter per zone; throws a RangeError for a zone the browser lacks.
function fmtFor(zone: string): Intl.DateTimeFormat {
  let f = fmtCache.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    });
    fmtCache.set(zone, f);
  }
  return f;
}

export function isValidZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || !zone) return false;
  try { fmtFor(zone); return true; } catch { return false; }
}

// One spelling per zone (Asia/Kolkata and Asia/Calcutta are the same zone), so
// a city is recognised whichever name it was saved under.
export function canonicalZone(zone: string): string {
  try { return fmtFor(zone).resolvedOptions().timeZone; } catch { return zone; }
}

// The device's zone: the user's own time.
export function localTimeZone(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

// "America/New_York" -> "New York", "America/Argentina/Buenos_Aires" -> "Buenos Aires".
export function cityOf(zone: string): string {
  if (/^(Etc\/)?(UTC|UCT|GMT|Universal|Zulu)$/.test(zone)) return 'UTC';
  const last = zone.split('/').pop() || zone;
  return RENAMED[last] ?? last.replace(/_/g, ' ');
}

type Wall = { y: number; mo: number; d: number; h: number; mi: number };
// The date and time on the wall clock in `zone` at the instant `at`.
function wallClock(at: Date, zone: string): Wall {
  const p: Partial<Record<Intl.DateTimeFormatPartTypes, number>> = {};
  for (const part of fmtFor(zone).formatToParts(at)) if (part.type !== 'literal') p[part.type] = Number(part.value);
  return { y: p.year ?? 1970, mo: p.month ?? 1, d: p.day ?? 1, h: (p.hour ?? 0) % 24, mi: p.minute ?? 0 };
}
const dayNumber = (w: Wall) => Date.UTC(w.y, w.mo - 1, w.d) / 86_400_000;
const offsetOf = (w: Wall, at: Date) =>
  Math.round((Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi) - Math.floor(at.getTime() / 60_000) * 60_000) / 60_000);

// Minutes ahead of UTC in `zone` at `at`: +540 Tokyo, -240 New York in summer.
export function utcOffsetMinutes(at: Date, zone: string): number {
  return offsetOf(wallClock(at, zone), at);
}

const MINUS = '−';
const hoursMinutes = (minutes: number) => ({ h: Math.floor(Math.abs(minutes) / 60), m: Math.abs(minutes) % 60 });

// The date there next to the user's: "Tomorrow" when it is already the next day.
export function dayLabel(days: number): string {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return `${days > 0 ? '+' : MINUS}${Math.abs(days)} days`;
}

// How far ahead or behind the user's own time: "+7h", "−4h", "+5h 30m".
export function diffLabel(minutes: number): string {
  if (minutes === 0) return 'Same time';
  const { h, m } = hoursMinutes(minutes);
  return `${minutes > 0 ? '+' : MINUS}${[h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ')}`;
}

// The same as a screen reader says it: "7 hours ahead", "30 minutes behind".
export function diffSpoken(minutes: number): string {
  if (minutes === 0) return 'same time';
  const { h, m } = hoursMinutes(minutes);
  const parts = [h ? `${h} hour${h === 1 ? '' : 's'}` : '', m ? `${m} minute${m === 1 ? '' : 's'}` : ''].filter(Boolean);
  return `${parts.join(' ')} ${minutes > 0 ? 'ahead' : 'behind'}`;
}

// "UTC+9", "UTC−4", "UTC+5:30", "UTC".
export function utcLabel(minutes: number): string {
  if (minutes === 0) return 'UTC';
  const { h, m } = hoursMinutes(minutes);
  return `UTC${minutes > 0 ? '+' : MINUS}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

export type ClockRow = ClockCity & {
  hm: string; ampm: 'am' | 'pm';   // "2:05" + "pm"
  iso: string;                     // "2026-10-21T14:05+09:00", for <time dateTime>
  day: string;                     // Today / Tomorrow / Yesterday
  diff: string; diffSpoken: string; // "+7h" / "7 hours ahead"; "Local" on the user's own zone
  utc: string;                     // "UTC+9"
  isLocal: boolean;
};

// Each city's row at `at`. The time of day follows the app's one convention,
// 12-hour with am/pm as every timestamp is written (shared/utils/date.ts
// formatDateTime: "2:05pm"); there is no company or user 12/24-hour setting.
export function clockRows(cities: ClockCity[], at: Date, localZone: string): ClockRow[] {
  const local = wallClock(at, localZone);
  const localOffset = offsetOf(local, at);
  const localCanon = canonicalZone(localZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return cities.map((c) => {
    const w = wallClock(at, c.zone);
    const offset = offsetOf(w, at);
    const isLocal = canonicalZone(c.zone) === localCanon;
    const { h, m } = hoursMinutes(offset);
    return {
      ...c,
      hm: `${w.h % 12 === 0 ? 12 : w.h % 12}:${pad(w.mi)}`,
      ampm: w.h < 12 ? 'am' : 'pm',
      iso: `${w.y}-${pad(w.mo)}-${pad(w.d)}T${pad(w.h)}:${pad(w.mi)}${offset === 0 ? 'Z' : `${offset > 0 ? '+' : '-'}${pad(h)}:${pad(m)}`}`,
      day: dayLabel(dayNumber(w) - dayNumber(local)),
      diff: isLocal ? 'Local' : diffLabel(offset - localOffset),
      diffSpoken: isLocal ? 'your local time' : diffSpoken(offset - localOffset),
      utc: utcLabel(offset),
      isLocal,
    };
  });
}

// The user's zone, then New York and London, without repeats (a user in New
// York gets New York and London).
export function defaultCities(localZone: string): ClockCity[] {
  const out: ClockCity[] = [];
  const seen = new Set<string>();
  for (const zone of [localZone, ...DEFAULT_ZONES]) {
    if (!isValidZone(zone) || seen.has(canonicalZone(zone))) continue;
    seen.add(canonicalZone(zone));
    out.push({ zone, label: cityOf(zone) });
  }
  return out;
}

// The tile's cities (config.cities): the saved list, skipping an entry this
// browser cannot show or a repeat, or, until the user edits it, the defaults.
export function citiesOf(config: Record<string, unknown> | undefined, localZone: string): ClockCity[] {
  const saved = config?.cities;
  if (!Array.isArray(saved)) return defaultCities(localZone);
  const out: ClockCity[] = [];
  for (const c of saved) {
    const zone = c && typeof c === 'object' ? (c as { zone?: unknown }).zone : undefined;
    if (!isValidZone(zone)) continue;
    const raw = (c as { label?: unknown }).label;
    const label = typeof raw === 'string' && raw.trim() ? raw.trim() : cityOf(zone);
    if (!out.some((o) => o.zone === zone && o.label === label)) out.push({ zone, label });
  }
  return out;
}

// The zone is already in the list (under any spelling).
export function hasZone(cities: ClockCity[], zone: string): boolean {
  const canon = canonicalZone(zone);
  return cities.some((c) => canonicalZone(c.zone) === canon);
}

type Indexed = ClockCity & { name: string; words: string[]; id: string };
const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
let catalog: Indexed[] | null = null;
function zoneCatalog(): Indexed[] {
  if (catalog) return catalog;
  let ids: string[] = [];
  try { ids = Intl.supportedValuesOf('timeZone'); } catch { /* older engine: the fallback list */ }
  const options: ClockCity[] = (ids.length ? ids : FALLBACK_ZONES.filter(isValidZone))
    .filter((zone) => zone.includes('/') && !zone.startsWith('Etc/'))
    .map((zone) => ({ zone, label: cityOf(zone) }));
  options.push({ zone: 'UTC', label: 'UTC' });
  for (const [label, zone] of ALIASES) if (isValidZone(zone)) options.push({ zone, label });
  catalog = options.map((o) => ({ ...o, name: fold(o.label), words: fold(o.label).split(/[\s.,'-]+/), id: fold(o.zone.replace(/_/g, ' ')) }));
  return catalog;
}

// Up to `limit` cities for what the user typed, best first: a name starting
// with it, then a word of a name, then anywhere in a name, then in the zone id
// ("america/", "calcutta"). Accents are ignored ("sao paulo" finds São Paulo).
export function searchZones(query: string, limit = 8): ClockCity[] {
  const q = fold(query.trim());
  if (!q) return [];
  const hits: Array<{ o: Indexed; rank: number }> = [];
  for (const o of zoneCatalog()) {
    const rank = o.name.startsWith(q) ? 0 : o.words.some((w) => w.startsWith(q)) ? 1 : o.name.includes(q) ? 2 : o.id.includes(q) ? 3 : -1;
    if (rank >= 0) hits.push({ o, rank });
  }
  hits.sort((a, b) => a.rank - b.rank || a.o.label.localeCompare(b.o.label) || a.o.zone.localeCompare(b.o.zone));
  return hits.slice(0, limit).map(({ o }) => ({ zone: o.zone, label: o.label }));
}
